import ConfiguratorComponent from './modules/configurator-component';
import PriceCalculation from './modules/price-calculation-matrix';

if (!customElements.get('matrix-configurator')) {
  class MatrixConfigurator extends ConfiguratorComponent {
    constructor() {
      super();

      // Selectors specific to the matrix configurator
      this.matrixColorPicker = document.querySelector('matrix-color-picker');
      this.colorPlaceholder = document.querySelector('[data-color-placeholder]');
      this.variantOptions = document.querySelectorAll('[data-variant-option]');
      this.widthHeightFields = document.querySelector('[data-step="size"]');
      this.productMedia = document.querySelector('[data-product-media]');

      // Steps
      const steps = this.defineSteps(this.productData['product_type']);
      const progressBarSteps = this.defineProgressBarSteps(this.productData['product_type']);
      if (!steps.length || !progressBarSteps.length) {
        return;
      }

      // Options
      this.options = {
        debug: false,
        steps: steps,
        progressBarSteps: progressBarSteps,
      };

      // state
      this.state = {
        step: 'size',
        progress: [],
        masterId: null,
      };

      // Get matrix metaobject data
      this.matrixData = this.getMatrixData();
      if (this.options.debug) console.log('matrix', this.matrixData);

      // Get surcharge data
      this.surchargeData = this.getSurchargeData();
      if (this.options.debug) console.log('surchargeData', this.surchargeData);

      // Get the master id
      this.masterId = this.getMasterId();
      this.state.masterId = this.masterId;

      // Free text input for location
      this.locationFreeInput = handleize(this.dataset.locationFreeInput || '');
    }

    /**
     * Define steps based on product type
     * @returns {Array} steps
     */
    defineSteps() {
      let steps = [...this.querySelectorAll('[data-step]')].map((step) =>
        step.getAttribute('data-step')
      );
      steps = steps.filter((step, index) => steps.indexOf(step) === index);
      return steps;
    }

    /**
     * Define progress bar steps based on product type
     * @returns {Array} steps
     */
    defineProgressBarSteps() {
      let steps = [...this.querySelectorAll('[data-progress]')].map((step) =>
        step.getAttribute('data-progress')
      );
      steps = steps.filter((step, index) => steps.indexOf(step) === index);
      return steps;
    }

    /**
     * Calculate curtain price
     * @returns {object} calculation
     */
    priceCalculation() {
      return new PriceCalculation(
        this.productData,
        this.matrixData,
        this.surchargeData,
        this.formData
      );
    }

    /**
     * @returns Matrix metaobject data
     */
    getMatrixData(html = this) {
      const matrixData = html.querySelector('[data-matrix-json]')
        ? parseJson(html.querySelector('[data-matrix-json]').textContent)
        : {};

      return matrixData;
    }

    /**
     * @returns Surcharge metaobject data
     */
    getSurchargeData() {
      const surchargeData = this.querySelector('[data-surcharges-json]')
        ? parseJson(this.querySelector('[data-surcharges-json]').textContent)
        : {};

      return surchargeData;
    }

    /**
     * @returns Master id
     */
    getMasterId() {
      return this.getAttribute('data-master-id');
    }

    /**
     * Format add to cart item with properties
     * @param {object} calculation
     * @returns {object} line item with properties
     */
    formatLineItem(calculation) {
      if (!this.formData || !calculation) {
        return;
      }

      // Return formatted line item with properties
      return {
        id: parseInt(this.formData.id),
        quantity: parseInt(this.formData.quantity),
        properties: {
          _ref: new Date().getTime(),
          _token: calculation.token,
          _compare_at_price: calculation.compare_at_price,
          _width: this.formData.width,
          _height: this.formData.height,
          ...(this.formData.product_type && {
            _product_type: this.formData.product_type,
          }),
          ...(this.formData.color && {
            _color: this.formData.color,
          }),
          ...(this.formData.material && {
            _material: this.formData.material,
          }),
          ...(this.formData.slat_width && {
            _slat_width: this.formData.slat_width,
          }),
          ...(this.formData.window_type && {
            _window_type: this.formData.window_type,
          }),
          ...(this.formData.placement && {
            _placement: this.formData.placement,
          }),
          ...(this.formData.chain && {
            _chain: this.formData.chain,
          }),
          ...(this.formData.control_direction && {
            _control_direction: this.formData.control_direction,
          }),
          ...(this.formData.control_location && {
            _control_location: this.formData.control_location,
          }),
          ...(this.formData.control_type && {
            _control_type: this.formData.control_type,
          }),
          ...(this.formData.sheeting_type && {
            _sheeting_type: this.formData.sheeting_type,
          }),
          ...(this.formData.tape && {
            _tape: this.formData.tape,
          }),
          ...(this.formData.option_id && {
            _option_id: this.formData.option_id,
          }),
          ...(this.formData.option_sku && {
            _option_sku: this.formData.option_sku,
          }),
          ...(this.formData.option_title && {
            _option_title: this.formData.option_title,
          }),
          ...(this.formData.option_color_name && {
            _option_color: this.formData.option_color_name,
          }),
          ...(this.formData.type_of_attachment && {
            _type_of_attachment: this.formData.type_of_attachment,
          }),
          ...(this.formData.draw_rod && {
            _draw_rod: this.formData.draw_rod,
          }),
          ...(this.formData.delivery_time && {
            _delivery_time: this.formData.delivery_time,
          }),
          ...(this.formData.location && {
            _location: this.formData.location,
          }),
          ...(this.formData.location_free_input && {
            _location_free_input: this.formData.location_free_input,
          }),
          [this.translateKeyValue('width')]: this.translateKeyValue(this.formData.width + ' cm'),
          [this.translateKeyValue('height')]: this.translateKeyValue(this.formData.height + ' cm'),
          ...(this.formData.color && {
            [this.translateKeyValue('color')]: this.translateKeyValue(this.formData.color),
          }),
          ...(this.formData.material && {
            [this.translateKeyValue('material')]: this.translateKeyValue(this.formData.material),
          }),
          ...(this.formData.slat_width && {
            [this.translateKeyValue('slat_width')]: this.translateKeyValue(
              this.formData.slat_width + ' mm'
            ),
          }),
          ...(this.formData.window_type && {
            [this.translateKeyValue('window_type')]: this.translateKeyValue(
              this.formData.window_type
            ),
          }),
          ...(this.formData.placement && {
            [this.translateKeyValue('placement')]: this.translateKeyValue(this.formData.placement),
          }),
          ...(this.formData.control && {
            [this.translateKeyValue('control')]: this.translateKeyValue(this.formData.control),
          }),
          ...(this.formData.control_direction && {
            [this.translateKeyValue('control_direction')]: this.translateKeyValue(
              this.formData.control_direction
            ),
          }),
          ...(this.formData.control_location && {
            [this.translateKeyValue('control_location')]: this.translateKeyValue(
              this.formData.control_location
            ),
          }),
          ...(this.formData.control_type && {
            [this.translateKeyValue('control_type')]: this.translateKeyValue(
              this.formData.control_type
            ),
          }),
          ...(this.formData.chain && {
            [this.translateKeyValue('chain')]: this.translateKeyValue(this.formData.chain),
          }),
          ...(this.formData.sheeting_type && {
            [this.translateKeyValue('sheeting_type')]: this.translateKeyValue(
              this.formData.sheeting_type
            ),
          }),
          ...(this.formData.tape && {
            [this.translateKeyValue('tape')]: this.translateKeyValue(this.formData.tape),
          }),
          ...(this.formData.option_color_name && {
            [this.translateKeyValue('option_color_name')]: this.translateKeyValue(
              this.formData.option_color_name
            ),
          }),
          ...(this.formData.type_of_attachment && {
            [this.translateKeyValue('type_of_attachment')]: this.translateKeyValue(
              this.formData.type_of_attachment
            ),
          }),
          ...(this.formData.draw_rod && {
            [this.translateKeyValue('draw_rod')]: this.translateKeyValue(this.formData.draw_rod),
          }),
          ...(this.formData.location &&
            !this.formData.location_free_input && {
              [this.translateKeyValue('location')]: this.translateKeyValue(this.formData.location),
            }),
          ...(this.formData.location_free_input && {
            [this.translateKeyValue('location')]: this.translateKeyValue(
              this.formData.location_free_input
            ),
          }),
        },
      };
    }

    /**
     * Handle errors from price calculation
     * @param {Array} errors
     */
    handleErrors(errors) {
      super.handleErrors(errors);

      errors.map((error) => {
        const message = this.translations[error.handle]
          ? this.translations[error.handle]
          : error.message;
        switch (error.type) {
          case 'width':
            if (this.formData.width != '') {
              this.querySelector('[name="width"]').setAttribute('aria-invalid', true);
              this.querySelector('[name="width"]').classList.add('error');
            }
            break;

          case 'height':
            if (this.formData.height != '') {
              this.querySelector('[name="height"]').setAttribute('aria-invalid', true);
              this.querySelector('[name="height"]').classList.add('error');
            }
            break;
        }
      });
    }

    /**
     * Handle variant change
     * @param {HTMLElement} input
     */
    async handleVariantChange(input) {
      if (!input || !input.hasAttribute('data-variant-option')) return;

      // Ge tthe selected variant
      const selectedVariant = this.getSelectedVariant({ target: input });
      const variantId = selectedVariant.id;

      // Return if no variant is found
      if (!variantId) return;
      if (variantId == this.state.masterId) return;

      this.options.debug && console.log('New variant', variantId);

      // Update the master id
      this.state.masterId = variantId;

      // Update the url
      window.history.replaceState({}, '', `${window.location.pathname}?variant=${variantId}`);

      // Update the variant input
      this.querySelector('[name="id"]').value = variantId;

      // Fetch and update the product and price matrix data
      try {
        const response = await fetch(
          `${window.location.pathname}?variant=${variantId}&sections[]=matrix-configurator-data&sections[]=${this.sectionId}`
        );
        const data = await response.json();

        // Update the product and price matrix data
        if (data['matrix-configurator-data']) {
          const section = data['matrix-configurator-data'];
          const html = new DOMParser().parseFromString(section, 'text/html');

          // Update the product and price matrix data
          this.productData = this.getProductData(html);
          this.matrixData = this.getMatrixData(html);
          this.options.debug && console.log('Updated productData', this.productData);
          this.options.debug && console.log('Updated matrixData', this.matrixData);

          // Update the available variant options, product title, min max width height and delivery time
          this.updateAvailableVariantOptions(input, selectedVariant, selectedVariant.options);
          this.updateProductTitle();
          this.updateMinMaxWidthHeight();
          this.updateDeliveryTime();
        }

        // Update the product media
        if (data[this.sectionId] && this.productMedia) {
          const productSection = data[this.sectionId];
          const productHtml = new DOMParser().parseFromString(productSection, 'text/html');
          const media = productHtml.querySelector('[data-product-media]').innerHTML;
          this.productMedia.innerHTML = media;
        }
      } catch (error) {
        console.error(error);
        this.handleErrors([
          {
            type: 'general',
            handle: 'something_went_wrong',
            message: `Sorry, we couldn't find the price data.`,
          },
        ]);
      }
    }

    /**
     * Get the selected variant
     * Match the variant id from the variantsJson
     * @param {Event} event - The event object.
     */
    getSelectedVariant({ target }) {
      const variantsJson = this.productData.variants;

      if (!variantsJson) return;

      // Get the options that are checked
      const options = [...this.variantOptions]
        .filter((option) => option.checked)
        .map((option) => ({
          updated: option.value === target.value,
          option: option.name,
          value: option.value,
        }));

      // First match the updated option from the target
      let variants = variantsJson.filter((variant) => {
        return variant.options.includes(target.value);
      });

      // Then if we have multiple variants, filter further using the first other option
      if (variants.length > 1) {
        const variants2 = variants.filter((variant) => {
          const value = options.find((option) => !option.updated)?.value;
          return variant.options.includes(value);
        });
        variants = variants2.length === 0 ? variants : variants2;
      }

      // Then if we have multiple variants, filter further using the last other option
      if (variants.length > 1) {
        const variants3 = variants.filter((variant) => {
          const value = options.findLast((option) => !option.updated)?.value;
          return variant.options.includes(value);
        });
        variants = variants3.length === 0 ? variants : variants3;
      }

      return variants[0];
    }

    /**
     * Update the variant available variant options
     * @param {HTMLElement} target - The target element.
     * @param {Variant} variant - The variant object.
     * @param {Array} optionValues - The option values.
     */
    updateAvailableVariantOptions(target, variant, optionValues) {
      if (!target || !optionValues) return;

      // Get the variants
      const variantsJson = this.productData.variants;

      // Loop over the variant options
      this.querySelectorAll('[data-variant-option]').forEach((option) => {
        // If the option is in the same group
        if (option.dataset.variantGroup === target.dataset.variantGroup) {
          // Ensure the target option is checked and the other options in the group not
          option.checked = option === target;
          return;
        }

        // If the option is in a group before the target group, enable the option
        if (parseInt(option.dataset.variantGroup) <= parseInt(target.dataset.variantGroup)) {
          option.classList.toggle('disabled', false);
          return;
        }

        // Disable the option if it's not any variant based on the variant options and the two other active options
        const disabled = !variantsJson.find((variant) => {
          const index = variant.options.indexOf(option.value);
          const otherOptions = variant.options.filter((value, i) => i !== index);

          const disabled =
            otherOptions.every((value) => optionValues.includes(value)) &&
            variant.options.includes(option.value) &&
            (variant.available === true || !this.disableBasedOnVariantAvailability);
          return disabled;
        });
        if (disabled) {
          option.checked = false;
          option.disabled = true;
          option.closest('.option').classList.add('hidden');
        } else {
          option.disabled = false;
          option.closest('.option').classList.remove('hidden');
        }
      });
    }

    /**
     * Handle next steps
     * @param {HTMLElement} input
     */
    handleSteps(input) {
      const step = input ? input.closest('[data-step]').dataset.step : this.state.step;
      const nextStep = this.options.steps[this.options.steps.indexOf(step) + 1];
      const progressStep =
        input && input.closest('[data-progress]')
          ? input.closest('[data-progress]').dataset.progress
          : this.state.progress[this.state.progress.length - 1];
      const nextProgressStep =
        this.options.progressBarSteps[this.options.progressBarSteps.indexOf(progressStep) + 1];

      switch (step) {
        case 'size':
          if (this.formData.width != '' && this.formData.height != '') {
            this.setNextStep(nextStep);
            this.setProgressBar(progressStep); // size
          }
          break;
        case 'placement':
          if (this.formData.placement != '') {
            this.setNextStep(nextStep); // chain or tape
            this.setProgressBar(progressStep); // placement
          }
          break;
        case 'chain':
          if (this.formData.chain != '') {
            this.setNextStep(nextStep); // control location
            this.setProgressBar(progressStep); // chain
          }
          break;
        case 'draw_rod':
          if (this.formData.draw_rod != '') {
            this.setNextStep(nextStep);
            this.setProgressBar(progressStep); // draw rod
          }
          break;
        case 'type_of_attachment':
          if (this.formData.type_of_attachment != '') {
            this.setNextStep(nextStep);
            this.setProgressBar(progressStep); // type of attachment
          }
          break;
        case 'option_id':
          if (this.formData.option_id != '' && this.formData.option_title != '') {
            this.setNextStep(nextStep); // quantity or control location blinds
            this.setProgressBar(progressStep); // sheeting or tape
          }
          break;
        case 'control':
          this.updateProductTitle();
          if (this.formData.control != '') {
            this.setNextStep(nextStep); // control location
            this.setProgressBar(progressStep); // control direction
            if (handleize(this.formData.control_direction) == '') {
              this.setProgressBar('control_direction', false); // remove control location step in progress
            }
            if (handleize(this.formData.control_direction) != 'top-down') {
              this.setPrevStep('control_location');
              if (handleize(this.formData.control_location) != '') {
                this.setProgressBar('control_direction', false); // remove control location step in progress
              }
              this.querySelectorAll('[name="control_location"]').forEach((input) => {
                input.checked = false;
              });
            }
          }
          break;
        case 'control_location':
          if (this.formData.control_location != '') {
            this.setNextStep(nextStep); // sheeting_type
            this.setProgressBar(progressStep);
          }
          break;
        case 'control_type':
          if (this.formData.control_type != '') {
            this.setNextStep(nextStep); // quantity
            this.setProgressBar(progressStep); // control type
          }
          break;
        case 'control_direction':
          if (this.options.steps.includes('control_location')) {
            if (handleize(this.formData.control_direction) == 'top-down') {
              this.setNextStep(nextStep);
              this.setProgressBar(progressStep, false); // remove control step in progress
            } else if (this.formData.control_direction != '') {
              const skipStep = this.options.steps[this.options.steps.indexOf(step) + 2]; // Skip the control location field

              this.setNextStep(skipStep); // hardware colors
              this.setPrevStep(nextStep); // To hide the option id field
              this.setProgressBar(progressStep); // control direction
            }
            // Clear the control location field
            this.querySelectorAll('[name="control_location"]').forEach((input) => {
              input.checked = false;
            });
          } else if (this.formData.control_direction != '') {
            this.setNextStep(nextStep); // control location
            this.setProgressBar(progressStep); // control direction
          }
          break;
        case 'sheeting_type':
          if (!this.options.steps.includes('option_id') && this.formData.sheeting_type != '') {
            this.setNextStep(nextStep); // quantity
            this.setProgressBar(progressStep); // sheeting
          } else if (this.formData.sheeting_type == 'none') {
            const skipStep = this.options.steps[this.options.steps.indexOf(step) + 2]; // Skip option_id field
            this.setNextStep(skipStep); // quantity
            this.setPrevStep(nextStep); // To hide the option id field
            this.setProgressBar(progressStep); // sheeting
            // Clear the color placeholder
            this.colorPlaceholder.querySelector('input[name="option_title"]').value = '';
            this.colorPlaceholder.querySelector('input[name="option_color_name"]').value = '';
            this.colorPlaceholder.querySelector('input[name="option_sku"]').value = '';
          } else if (this.formData.sheeting_type != '') {
            this.setNextStep(nextStep); // option_id
            this.setProgressBar(progressStep, false); // remove sheeting step in progress
            this.matrixColorPicker.filter(this.formData.sheeting_type);
          }
          if (this.options.steps.includes('option_id')) {
            this.matrixColorPicker.clear();
          }
          break;
        case 'tape':
          // If no options step go to next step
          if (!this.options.steps.includes('option_id') && this.formData.tape != '') {
            this.setNextStep(nextStep); // control location 2
            this.setProgressBar(progressStep); // tape
          } else if (this.formData.tape == 'no') {
            const skipStep = this.options.steps[this.options.steps.indexOf(step) + 2]; // Skip option_id field
            this.setNextStep(skipStep); // control location 2
            this.setPrevStep(nextStep); // To hide the option id field
            this.setProgressBar(progressStep); // tape
          } else if (this.formData.tape != '') {
            this.setNextStep(nextStep); // option_id
            this.setProgressBar(progressStep, false); // remove sheeting step in progress
            this.matrixColorPicker.filter('blind');
          }
          if (this.options.steps.includes('option_id')) {
            this.matrixColorPicker.clear();
          }
          break;
        case 'location':
          if (
            this.locationFreeInput &&
            handleize(this.formData.location) == this.locationFreeInput
          ) {
            this.setNextStep(nextStep); // quantity
          } else if (this.formData.location != '') {
            const skipStep = this.options.steps[this.options.steps.indexOf(step) + 1]; // Skip the location free text field
            this.setNextStep(skipStep); // quantity
            this.setPrevStep(nextStep); // To hide the location free text field
            // Clear the location free text field
            if (this.querySelector('[name="location_free_input"]')) {
              setTimeout(() => {
                this.querySelector('[name="location_free_input"]').value = '';
              }, 300);
            }
          }
          break;
        case 'location_free_input':
          if (this.formData.location_free_input != '') {
            this.setNextStep(nextStep); // color
          }
          break;
      }

      // Toggle submit button
      const submitState = this.state.progress.length == this.options.progressBarSteps.length;
      this.toggleSubmitButton(submitState);
    }

    /**
     * Set progress bar step
     * @param {string} step
     * @param {boolean} add - add step from progress array
     */
    setProgressBar(step, add = true) {
      if (!step) return;

      // Remove or add step to progress array
      if (!add) {
        this.state.progress = this.state.progress.filter((item) => item !== step);
      } else if (!this.state.progress.includes(step)) {
        this.state.progress.push(step);
      }

      // Calculate progress bar width
      const progress = (this.state.progress.length / this.options.progressBarSteps.length) * 100;

      // Set progress bar width
      this.progressBar.style.width = `${progress}%`;
    }

    /**
     * Update product title
     */
    updateProductTitle() {
      if (!this.productData.title || this.productData.variant_title == '') return;
      const title = `${this.productData.title} - ${this.productData.variant_title.replace(
        ' / ',
        ' - '
      )}`;
      document.querySelector('[data-product-title]').innerHTML = title;
    }

    /**
     * Update the min and max width and height
     */
    updateMinMaxWidthHeight() {
      // Get the min and max width and height
      const minWidth = this.productData.min_width;
      const maxWidth = this.productData.max_width;
      const minHeight = this.productData.min_height;
      const maxHeight = this.productData.max_height;
      if (!minWidth || !maxWidth || !minHeight || !maxHeight) return;

      // Get the width and height fields
      if (!this.widthHeightFields) return;
      const widthField = this.widthHeightFields.querySelector('[name="width"]');
      const heightField = this.widthHeightFields.querySelector('[name="height"]');
      const widthErrorField = this.widthHeightFields.querySelector('[data-error="width"]');
      const heightErrorField = this.widthHeightFields.querySelector('[data-error="height"]');
      if (!widthField || !heightField || !widthErrorField || !heightErrorField) return;

      // Get the translations
      const translations = this.widthHeightFields.dataset.translations;
      if (!translations) return;
      const translationsObj = JSON.parse(translations || '{}');
      const widthError = translationsObj.width_error
        .replace('{{ variable_1 }}', minWidth)
        .replace('{{ variable_2 }}', maxWidth);
      const heightError = translationsObj.height_error
        .replace('{{ variable_1 }}', minHeight)
        .replace('{{ variable_2 }}', maxHeight);
      const widthPlaceholder = translationsObj.width_placeholder
        .replace('{{ variable_1 }}', minWidth)
        .replace('{{ variable_2 }}', maxWidth);
      const heightPlaceholder = translationsObj.height_placeholder
        .replace('{{ variable_1 }}', minHeight)
        .replace('{{ variable_2 }}', maxHeight);

      // Set the width and height fields
      widthField.setAttribute('min', minWidth);
      widthField.setAttribute('max', maxWidth);
      heightField.setAttribute('min', minHeight);
      heightField.setAttribute('max', maxHeight);
      widthField.setAttribute('placeholder', widthPlaceholder);
      heightField.setAttribute('placeholder', heightPlaceholder);

      // Set the width and height errors
      widthErrorField.innerText = widthError;
      heightErrorField.innerText = heightError;

      // Remove the error classes from the width and height fields (will be revalidated later in the price calculation)
      widthField.classList.remove('error');
      widthField.setAttribute('aria-invalid', false);
      heightField.classList.remove('error');
      heightField.setAttribute('aria-invalid', false);
    }

    /**
     * Update the delivery time
     */
    updateDeliveryTime() {
      if (!this.productData.delivery_time) return;
      const deliveryTimeField = this.querySelector('[name="delivery_time"]');
      if (!deliveryTimeField) return;
      deliveryTimeField.value = this.productData.delivery_time;
    }
  }

  window.MatrixConfigurator = MatrixConfigurator;

  customElements.define('matrix-configurator', MatrixConfigurator);
}
